/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';

import {
    Box,
    Stack,
    FauxImage,
    FauxUnavailableImage,
    Button,
    Text,
    Heading,
    DividerNew as Divider,
    Placeholder,
    PlaceholderText,
    Frame,
} from 'sarsaparilla';
import { BuyNowButton } from '../BuyNow';
import { useBuyNowRegister } from './BuyNowContext';

const modificationButtonLabels = ['Save Changes', 'Apply Changes'];

export function OrderDetailsSummary({
    imageUrl,
    heading,
    parentName,
    children,
    onProceedToCartClick,
    isProceedToCartDisabled,
    isLoading,
    proceedToCartButtonText = 'Proceed to Cart',
    secondaryButtonText = 'Continue Shopping',
    onSecondaryButtonClick,
    isWaitingForServerResponse,
    secondaryButtonIsDisabled,
    showBuyNow = false,
    reservationId,
}) {
    if (modificationButtonLabels.includes(proceedToCartButtonText)) {
        showBuyNow = false;
    }

    useBuyNowRegister(showBuyNow, reservationId);

    if (isLoading) {
        return <OrderDetailSummaryPlaceholder />;
    }
    return (
        <div data-component="OrderDetailsSummary">
            <Box border="gray" background="white">
                {!imageUrl && <FauxUnavailableImage aspectRatio="21:9" />}
                {!!imageUrl && (
                    <FauxImage aspectRatio="21:9" src={imageUrl} altText={heading} />
                )}

                <div className="inner-padded">
                    <Stack>
                        <Stack space="xxs">
                            <Heading headingLevel={2} appearance="h6">
                                {heading}
                                <span className="rec-sr-only">Order Details Summary</span>
                            </Heading>
                            {parentName && (
                                <Text size="sm" color="gray">
                                    Part of {parentName}
                                </Text>
                            )}
                        </Stack>
                        <Divider />
                        {children}

                        <Stack space="xs">
                            <Button
                                isLoading={isWaitingForServerResponse}
                                onClick={() => onProceedToCartClick({ navigate: true })}
                                isDisabled={isProceedToCartDisabled}
                                data-testid="OrderDetailsSummary-cart-btn"
                                shouldFitContainer
                            >
                                {proceedToCartButtonText}
                            </Button>
                            {showBuyNow && (
                                <BuyNowButton
                                    onProceedToCartClick={onProceedToCartClick}
                                    isDisabled={isProceedToCartDisabled}
                                />
                            )}
                            {!!onSecondaryButtonClick && (
                                <Button
                                    onClick={onSecondaryButtonClick}
                                    isDisabled={secondaryButtonIsDisabled}
                                    appearance="link"
                                >
                                    {secondaryButtonText}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </div>
            </Box>
        </div>
    );
}

OrderDetailsSummary.propTypes = {
    onProceedToCartClick: PropTypes.func,
    onSecondaryButtonClick: PropTypes.func,
    heading: PropTypes.node,
    parentName: PropTypes.node,
    children: PropTypes.node,
    imageUrl: PropTypes.string,
    secondaryButtonText: PropTypes.string,
    isProceedToCartDisabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isWaitingForServerResponse: PropTypes.bool,
    proceedToCartButtonText: PropTypes.string,
    secondaryButtonIsDisabled: PropTypes.bool,
    showBuyNow: PropTypes.bool,
    reservationId: PropTypes.string,
};

function OrderDetailSummaryPlaceholder() {
    return (
        <div data-component="OrderDetailsSummary">
            <Box border="gray" background="white">
                <Frame aspectRatio="21:9">
                    <Placeholder />
                </Frame>

                <div className="inner-padded">
                    <Stack>
                        <Stack space="xxs">
                            <PlaceholderText fontSize={16} width="80%" />
                            <PlaceholderText fontSize={11} width="50%" />
                        </Stack>

                        <Divider />

                        <PlaceholderText lines={4} />

                        <Stack space="xs">
                            <Placeholder height="4rem" />
                            <Placeholder height="4rem" />
                        </Stack>
                    </Stack>
                </div>
            </Box>
        </div>
    );
}
